/**
 * F-131 SlotPicker — date selector + free-slots list with 5-min hold timer.
 *
 * Two-phase flow:
 *   1. User picks a date (next 30 days).
 *   2. We fetch `/specialists/<slug>/slots/?from=&to=` for that day.
 *   3. Tapping a slot calls `holdSlot(orderId, slotId)` and starts the
 *      5-minute countdown. Confirm/cancel triggers `onConfirm/onCancel`.
 *
 * Designed to be embedded in OrderProposal composer or OrderDetailPage
 * proposal mode — both must pass `orderId` once the draft order exists.
 */
import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import uk from 'dayjs/locale/uk';
import {
  fetchSpecialistSlots,
  holdSlot,
  releaseHold,
  type Slot,
} from '@/api/booking';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import styles from './SlotPicker.module.css';

// F-190 QA: ensure UA locale is registered even if AppShell-side init was
// tree-shaken out of this chunk. Per-call `.locale('uk')` on the format
// expression below guarantees the UA labels regardless of global default.
dayjs.locale('uk', uk);

export interface SlotPickerProps {
  specialistSlug: string;
  orderId?: number;
  onConfirm: (slot: Slot) => void;
  onCancel?: () => void;
}

const HOLD_TTL_SECONDS = 5 * 60;

export function SlotPicker({
  specialistSlug,
  orderId,
  onConfirm,
  onCancel,
}: SlotPickerProps): JSX.Element {
  const [allSlots, setAllSlots] = useState<Slot[]>([]);
  const [day, setDay] = useState<dayjs.Dayjs | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [held, setHeld] = useState<Slot | null>(null);
  const [remainingSec, setRemainingSec] = useState(HOLD_TTL_SECONDS);

  // F-190 QA: fetch the full 30-day window up front instead of paginating
  // by day. Lets us hide days with zero free slots completely — earlier
  // the picker rendered 30 chips and 28 of them said "немає вільних
  // слотів", which the user (rightly) called a dead-end UX.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const from = dayjs().startOf('day').toISOString();
    const to = dayjs().add(30, 'day').endOf('day').toISOString();
    fetchSpecialistSlots(specialistSlug, { from, to })
      .then((res) => {
        if (cancelled) return;
        setAllSlots(res.results);
      })
      .catch(() => {
        if (!cancelled) setError('Не вдалось завантажити слоти');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [specialistSlug]);

  // Bookable days = days that hold at least one slot whose status the
  // backend hasn't already marked "booked". `free` and `held` both pass
  // — `held` means another client is mid-flow and may release.
  const availableDays = useMemo(() => {
    const seen = new Map<string, dayjs.Dayjs>();
    for (const s of allSlots) {
      if (s.status === 'booked') continue;
      const start = dayjs(s.start_at).startOf('day');
      const key = start.format('YYYY-MM-DD');
      if (!seen.has(key)) seen.set(key, start);
    }
    return Array.from(seen.values()).sort((a, b) => a.valueOf() - b.valueOf());
  }, [allSlots]);

  // Auto-select the first available day so the slot grid is filled on
  // open instead of stuck on an empty initial state.
  useEffect(() => {
    if (day == null && availableDays.length > 0) setDay(availableDays[0]!);
  }, [day, availableDays]);

  const slots = useMemo(() => {
    if (day == null) return [];
    const key = day.format('YYYY-MM-DD');
    return allSlots.filter(
      (s) => dayjs(s.start_at).format('YYYY-MM-DD') === key && s.status !== 'booked',
    );
  }, [day, allSlots]);

  useEffect(() => {
    if (!held) return undefined;
    setRemainingSec(HOLD_TTL_SECONDS);
    const interval = window.setInterval(() => {
      setRemainingSec((s) => {
        if (s <= 1) {
          window.clearInterval(interval);
          setHeld(null);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, [held]);

  async function handlePick(slot: Slot): Promise<void> {
    if (!orderId) {
      // Without an orderId we can't hold — caller wants raw pick.
      onConfirm(slot);
      return;
    }
    try {
      await holdSlot(orderId, slot.id);
      setHeld(slot);
    } catch {
      setError('Слот уже зайнятий, спробуй інший');
    }
  }

  async function handleConfirm(): Promise<void> {
    if (!held) return;
    onConfirm(held);
  }

  async function handleCancel(): Promise<void> {
    if (held && orderId) {
      try {
        await releaseHold(orderId, held.id);
      } catch {
        /* ignore — server may release on its own */
      }
    }
    setHeld(null);
    onCancel?.();
  }

  const minutes = Math.floor(remainingSec / 60);
  const seconds = remainingSec % 60;

  return (
    <div className={styles.root}>
      {availableDays.length > 0 && (
        <div className={styles.dayRow}>
          {availableDays.map((d) => (
            <button
              key={d.toISOString()}
              type="button"
              className={[
                styles.dayChip,
                day && d.isSame(day, 'day') ? styles.dayChipActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => setDay(d)}
            >
              <span className={styles.dayLabel}>{d.locale('uk').format('dd')}</span>
              <span className={styles.dayDate}>{d.locale('uk').format('D MMM')}</span>
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className={styles.note}>Завантаження…</div>
      ) : error ? (
        <div className={styles.error}>{error}</div>
      ) : availableDays.length === 0 ? (
        <div className={styles.note}>
          У найближчі 30 днів вільних слотів немає. Напиши майстру в чат —
          можливо, домовитесь поза розкладом.
        </div>
      ) : slots.length === 0 ? (
        <div className={styles.note}>Обери день вище</div>
      ) : (
        <div className={styles.slotGrid}>
          {slots.map((s) => (
            <button
              key={s.id}
              type="button"
              className={[
                styles.slotBtn,
                held?.id === s.id ? styles.slotBtnActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
              disabled={!!held && held.id !== s.id}
              onClick={() => handlePick(s)}
            >
              {dayjs(s.start_at).format('HH:mm')}
            </button>
          ))}
        </div>
      )}

      {held && (
        <div className={styles.holdBar}>
          <Icon name="calendar" />
          <span className={styles.holdText}>
            Слот зарезервовано на {String(minutes).padStart(2, '0')}:
            {String(seconds).padStart(2, '0')}
          </span>
          <Button onClick={handleConfirm}>Підтвердити</Button>
          <Button variant="secondary" onClick={handleCancel}>
            Скасувати
          </Button>
        </div>
      )}
    </div>
  );
}
